/**
 * Full, standalone partner dashboard rendered at /partner for a signed-in
 * participant (verified ton_proof + a bought ticket). This intentionally
 * REPLACES the marketing landing with a dedicated professional tool shell — its
 * own app bar and layout — instead of embedding widgets into the landing page.
 */
import { useTranslation } from 'react-i18next';
import { TonConnectButton, useTonAddress } from '@tonconnect/ui-react';
import { ArrowLeft, KeyRound, LayoutDashboard } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { LegalLinks } from '../components/LegalLinks';
import { PyramidSpinner } from '../components/PyramidSpinner';
import { TON_NETWORK } from '../ton/config';
import { PartnerDashboard } from './PartnerDashboard';
import type { PartnerAuth } from './usePartnerAuth';

function shortAddress(address: string): string {
  return address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;
}

export function PartnerDashboardPage({
  refKey,
  referralsCount,
  referralEarnings,
  auth,
}: {
  refKey: number;
  referralsCount: number;
  referralEarnings: number;
  auth: PartnerAuth;
}) {
  const { t } = useTranslation();
  const walletAddress = useTonAddress();
  const address = auth.address;

  return (
    <div className="pl-page pl-dashpage">
      <header className="pl-dashbar">
        <a className="pl-header__brand" href="https://pyramida.app/partner" aria-label="Pyramida">
          <PyramidSpinner className="pl-brand-pyramid" />
          <img src="/text-logo.webp" alt="PRMD" className="pl-header__logo" />
          <span className="pl-dashbar__title">
            <LayoutDashboard size={15} aria-hidden="true" />
            {t('partner.panel.title')}
          </span>
          {TON_NETWORK !== 'mainnet' && <span className="pl-header__net">{TON_NETWORK}</span>}
        </a>
        <div className="pl-dashbar__right">
          <a className="pl-dashbar__back" href="/" title={t('partner.backToApp')}>
            <ArrowLeft size={14} aria-hidden="true" />
            <span className="pl-dashbar__back-label">{t('partner.backToApp')}</span>
          </a>
          <span className="pl-dashbar__id" title={address}>
            <KeyRound size={14} aria-hidden="true" />
            <strong>#{refKey}</strong>
            {address && <code>{shortAddress(address)}</code>}
          </span>
          <LanguageSwitcher />
          {walletAddress && <TonConnectButton className="ton-connect-compact" />}
        </div>
      </header>

      <main className="pl-dashmain">
        <PartnerDashboard
          refKey={refKey}
          referralsCount={referralsCount}
          referralEarnings={referralEarnings}
          auth={auth}
        />
        <LegalLinks className="legal-links--partner legal-links--dashboard" />
      </main>
    </div>
  );
}
